import {match} from 'ts-pattern';
import type {StoreApi} from 'zustand/vanilla';

import type {EagGridState, EagGridStore} from '../createEagGridStore';

type SelectionActions = Pick<
  EagGridStore,
  | 'setSelectedRowIds'
  | 'selectAllFiltered'
  | 'toggleRowSelection'
  | 'selectRowRange'
  | 'selectAll'
  | 'deselectAll'
  | 'setSingleSelection'
>;

/** Selection actions, including the current-page anchor for checkbox ranges. */
export function createSelectionActions(
  store: StoreApi<EagGridState>,
  selectAllFilteredSelected: (state: EagGridState) => boolean
): SelectionActions {
  const toggleRowSelection = (id: string) => {
    const state = store.getState();
    const next = selectAllFilteredSelected(state)
      ? new Set(state.rows.map((row) => row.id))
      : new Set(state.selectedRowIds);
    match(next.has(id))
      .with(true, () => next.delete(id))
      .with(false, () => next.add(id))
      .exhaustive();
    store.setState({
      selectedRowIds: next,
      bulkSelectionQueryId: null,
      rowSelectionAnchorId: id,
    });
  };

  return {
    setSelectedRowIds: (selectedRowIds: Set<string>) => {
      store.setState({selectedRowIds, bulkSelectionQueryId: null, rowSelectionAnchorId: null});
    },

    selectAllFiltered: () => {
      const state = store.getState();
      if (
        state.fetchState !== 'success' ||
        state.queryId === '' ||
        selectAllFilteredSelected(state)
      ) {
        return;
      }
      store.setState({
        bulkSelectionQueryId: state.queryId,
        selectedRowIds: new Set(state.rows.map((row) => row.id)),
        rowSelectionAnchorId: null,
      });
    },

    toggleRowSelection,

    selectRowRange: (id: string) => {
      const state = store.getState();
      const anchorIndex = state.rows.findIndex((row) => row.id === state.rowSelectionAnchorId);
      const targetIndex = state.rows.findIndex((row) => row.id === id);
      if (anchorIndex < 0 || targetIndex < 0) {
        store.setState({
          selectedRowIds: new Set([id]),
          bulkSelectionQueryId: null,
          rowSelectionAnchorId: id,
        });
        return;
      }
      const range = state.rows.slice(
        Math.min(anchorIndex, targetIndex),
        Math.max(anchorIndex, targetIndex) + 1
      );
      const next = new Set(range.map((row) => row.id));
      if (
        state.bulkSelectionQueryId === null &&
        next.size === state.selectedRowIds.size &&
        range.every((row) => state.selectedRowIds.has(row.id))
      ) {
        return;
      }
      store.setState({selectedRowIds: next, bulkSelectionQueryId: null});
    },

    selectAll: (ids: string[]) => {
      store.setState({
        selectedRowIds: new Set(ids),
        bulkSelectionQueryId: null,
        rowSelectionAnchorId: null,
      });
    },

    deselectAll: () => {
      const state = store.getState();
      if (
        state.selectedRowIds.size === 0 &&
        state.bulkSelectionQueryId === null &&
        state.rowSelectionAnchorId === null
      ) {
        return;
      }
      store.setState({
        selectedRowIds: state.selectedRowIds.size === 0 ? state.selectedRowIds : new Set(),
        bulkSelectionQueryId: null,
        rowSelectionAnchorId: null,
      });
    },

    setSingleSelection: (id: string) => {
      // SINGLE-mode policy lives in two places: this replace-on-click (with toggle-off)
      // and the click-parity normalization in utils/resolveExternalRowSelection.ts for
      // external requests (which deliberately has NO toggle — a declarative request for
      // an already-selected row must stay a no-op). Keep the two in sync when changing
      // SINGLE semantics.
      const current = store.getState().selectedRowIds;
      // Toggle off if already the only selected row
      if (current.size === 1 && current.has(id)) {
        store.setState({
          selectedRowIds: new Set(),
          bulkSelectionQueryId: null,
          rowSelectionAnchorId: null,
        });
        return;
      }
      store.setState({
        selectedRowIds: new Set([id]),
        bulkSelectionQueryId: null,
        rowSelectionAnchorId: null,
      });
    },
  };
}
